import { CheckCircleRounded, CloseOutlined, ErrorOutlineOutlined, ReplayOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, LinearProgress, Stack, Typography } from "@mui/material";

import { colors, semantic } from "../theme/tokens";

import { formatBytes } from "./model";
import type { QueueItem } from "./useDocuments";
import { FileTypeIcon } from "./ui";

// The upload queue panel: one row per file with its own progress, so a batch
// of two hundred product images never reads as "done" while three of them
// failed. Failed rows stay until retried or dismissed.

export interface UploadQueueProps {
    items: QueueItem[];
    onRetry: (id: string) => void;
    onDismiss: (id: string) => void;
    onClearDone: () => void;
}

export function UploadQueue({ items, onRetry, onDismiss, onClearDone }: UploadQueueProps) {
    if (items.length === 0) return null;
    const done = items.filter((it) => it.status === "done").length;
    const failed = items.filter((it) => it.status === "failed").length;
    const active = items.length - done - failed;

    let headline: string;
    if (active > 0) headline = `Uploading ${active === 1 ? "1 file" : `${active} files`}`;
    else if (failed > 0) headline = `${failed === 1 ? "1 file" : `${failed} files`} did not upload`;
    else headline = `${done === 1 ? "1 file" : `${done} files`} uploaded`;

    return (
        <Box sx={{ border: `1px solid ${failed > 0 && active === 0 ? semantic.warning.border : colors.line}`, borderRadius: "10px", bgcolor: colors.white, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderBottom: `1px solid ${colors.lineSoft}` }}>
                <Typography sx={{ fontWeight: 700, color: colors.navy, flex: 1 }}>{headline}</Typography>
                {done > 0 && active === 0 && (
                    <Button size="small" onClick={onClearDone} sx={{ textTransform: "none", fontWeight: 600 }}>
                        Clear finished
                    </Button>
                )}
            </Box>
            <Stack sx={{ maxHeight: 280, overflowY: "auto" }}>
                {items.map((it) => (
                    <Box key={it.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1, "& + &": { borderTop: `1px solid ${colors.lineSoft}` } }}>
                        <FileTypeIcon name={it.file.name} contentType={it.file.type} size={20} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {it.file.name}
                                <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
                                    {formatBytes(it.file.size)}
                                    {it.options.folderLabel ? ` · ${it.options.folderLabel}` : ""}
                                </Typography>
                            </Typography>
                            {it.status === "uploading" || it.status === "queued" ? (
                                <LinearProgress
                                    variant={it.status === "queued" ? "indeterminate" : "determinate"}
                                    value={Math.round(it.progress * 100)}
                                    sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: colors.lightGray, "& .MuiLinearProgress-bar": { bgcolor: colors.navy } }}
                                />
                            ) : it.status === "failed" ? (
                                <Typography variant="caption" sx={{ color: semantic.warning.text, display: "block" }}>
                                    {it.error}
                                </Typography>
                            ) : null}
                        </Box>
                        {it.status === "done" && <CheckCircleRounded sx={{ color: colors.navy, fontSize: 20 }} />}
                        {it.status === "failed" && (
                            <>
                                <ErrorOutlineOutlined sx={{ color: semantic.warning.border, fontSize: 20 }} />
                                <Button size="small" startIcon={<ReplayOutlined fontSize="small" />} onClick={() => onRetry(it.id)} sx={{ textTransform: "none", fontWeight: 600 }}>
                                    Retry
                                </Button>
                            </>
                        )}
                        {it.status !== "uploading" && (
                            <IconButton size="small" onClick={() => onDismiss(it.id)} aria-label="Dismiss">
                                <CloseOutlined fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
